import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Paper,
    Typography,
    Tabs,
    Tab,
    Button,
    IconButton,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import { hostelAPI, studentAPI } from '../services/api';
import { toast } from 'react-toastify';

const TabPanel = (props) => {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
};

const Hostel = () => {
    const [tabValue, setTabValue] = useState(0);
    const [hostels, setHostels] = useState([]);
    const [rooms, setRooms] = useState([]);
    const [allocations, setAllocations] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogType, setDialogType] = useState(''); // 'hostel', 'room', 'allocation'
    const [formData, setFormData] = useState({});
    const [students, setStudents] = useState([]);

    const fetchData = async () => {
        try {
            const [hostelsRes, roomsRes, allocationsRes, studentsRes] = await Promise.all([
                hostelAPI.getAll(),
                hostelAPI.getRooms(),
                hostelAPI.getAllocations(),
                studentAPI.getAll()
            ]);

            setHostels(hostelsRes.data.data);
            setRooms(roomsRes.data.data);
            setAllocations(allocationsRes.data.data);
            setStudents(studentsRes.data.data);
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleOpenDialog = (type, data = {}) => {
        setDialogType(type);
        setFormData(data);
        setOpenDialog(true);
    };

    const handleSubmit = async () => {
        try {
            if (formData.id) {
                if (dialogType === 'room') await hostelAPI.updateRoom(formData.id, formData);
                else if (dialogType === 'allocation') await hostelAPI.allocate(formData);
                else await hostelAPI.update(formData.id, formData);
                toast.success('Updated successfully');
            } else {
                if (dialogType === 'room') await hostelAPI.createRoom(formData);
                else if (dialogType === 'allocation') await hostelAPI.allocate(formData);
                else await hostelAPI.create(formData);
                toast.success('Created successfully');
            }

            setOpenDialog(false);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Operation failed');
        }
    };

    const handleDelete = async (type, id) => {
        if (!window.confirm('Are you sure?')) return;
        try {
            if (type === 'allocation') {
                await hostelAPI.deallocate(id);
            } else if (type === 'room') {
                await hostelAPI.deleteRoom(id);
            } else {
                await hostelAPI.delete(id);
            }

            toast.success('Operation successful');
            fetchData();
        } catch (error) {
            toast.error('Operation failed');
        }
    };

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h4" gutterBottom>
                Hostel Management
            </Typography>

            <Paper sx={{ width: '100%', mb: 2 }}>
                <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
                    <Tab label="Hostels" />
                    <Tab label="Rooms" />
                    <Tab label="Allocations" />
                </Tabs>

                {/* Hostels Tab */}
                <TabPanel value={tabValue} index={0}>
                    <Button startIcon={<AddIcon />} variant="contained" onClick={() => handleOpenDialog('hostel')} sx={{ mb: 2 }}>
                        Add Hostel
                    </Button>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Warden</TableCell>
                                    <TableCell>Capacity</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {hostels.map((hostel) => (
                                    <TableRow key={hostel.id}>
                                        <TableCell>{hostel.name}</TableCell>
                                        <TableCell>{hostel.type}</TableCell>
                                        <TableCell>{hostel.wardenName}</TableCell>
                                        <TableCell>{hostel.totalCapacity}</TableCell>
                                        <TableCell>
                                            <IconButton onClick={() => handleOpenDialog('hostel', hostel)}><EditIcon /></IconButton>
                                            <IconButton onClick={() => handleDelete('hostel', hostel.id)}><DeleteIcon /></IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </TabPanel>

                {/* Rooms Tab */}
                <TabPanel value={tabValue} index={1}>
                    <Button startIcon={<AddIcon />} variant="contained" onClick={() => handleOpenDialog('room')} sx={{ mb: 2 }}>
                        Add Room
                    </Button>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Room No</TableCell>
                                    <TableCell>Hostel</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Capacity</TableCell>
                                    <TableCell>Occupied</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rooms.map((room) => (
                                    <TableRow key={room.id}>
                                        <TableCell>{room.roomNumber}</TableCell>
                                        <TableCell>{room.hostel?.name}</TableCell>
                                        <TableCell>{room.type}</TableCell>
                                        <TableCell>{room.capacity}</TableCell>
                                        <TableCell>{room.occupied}</TableCell>
                                        <TableCell>
                                            <IconButton onClick={() => handleOpenDialog('room', room)}><EditIcon /></IconButton>
                                            <IconButton onClick={() => handleDelete('room', room.id)}><DeleteIcon /></IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </TabPanel>

                {/* Allocations Tab */}
                <TabPanel value={tabValue} index={2}>
                    <Button startIcon={<AddIcon />} variant="contained" onClick={() => handleOpenDialog('allocation')} sx={{ mb: 2 }}>
                        Allocate Room
                    </Button>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Student</TableCell>
                                    <TableCell>Room</TableCell>
                                    <TableCell>Hostel</TableCell>
                                    <TableCell>Start Date</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {allocations.map((alloc) => (
                                    <TableRow key={alloc.id}>
                                        <TableCell>{alloc.student?.firstName} {alloc.student?.lastName}</TableCell>
                                        <TableCell>{alloc.room?.roomNumber}</TableCell>
                                        <TableCell>{alloc.room?.hostel?.name}</TableCell>
                                        <TableCell>{alloc.startDate}</TableCell>
                                        <TableCell>
                                            <Chip label={alloc.status} color={alloc.status === 'Active' ? 'success' : 'default'} />
                                        </TableCell>
                                        <TableCell>
                                            {alloc.status === 'Active' && (
                                                <Button size="small" color="error" onClick={() => handleDelete('allocation', alloc.id)}>
                                                    Vacate
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </TabPanel>
            </Paper>

            {/* Dialog */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {formData.id ? 'Edit' : 'Add'} {dialogType.charAt(0).toUpperCase() + dialogType.slice(1)}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>

                        {dialogType === 'hostel' && (
                            <>
                                <TextField label="Name" fullWidth value={formData.name || ''} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
                                <TextField select label="Type" fullWidth value={formData.type || 'Boys'} onChange={(e) => setFormData({ ...formData, type: e.target.value })}>
                                    <MenuItem value="Boys">Boys</MenuItem>
                                    <MenuItem value="Girls">Girls</MenuItem>
                                    <MenuItem value="Co-ed">Co-ed</MenuItem>
                                </TextField>
                                <TextField label="Warden Name" fullWidth value={formData.wardenName || ''} onChange={(e) => setFormData({ ...formData, wardenName: e.target.value })} />
                                <TextField label="Capacity" type="number" fullWidth value={formData.totalCapacity || ''} onChange={(e) => setFormData({ ...formData, totalCapacity: e.target.value })} />
                            </>
                        )}

                        {dialogType === 'room' && (
                            <>
                                <TextField select label="Hostel" fullWidth value={formData.hostelId || ''} onChange={(e) => setFormData({ ...formData, hostelId: e.target.value })}>
                                    {hostels.map(h => <MenuItem key={h.id} value={h.id}>{h.name}</MenuItem>)}
                                </TextField>
                                <TextField label="Room Number" fullWidth value={formData.roomNumber || ''} onChange={(e) => setFormData({ ...formData, roomNumber: e.target.value })} />
                                <TextField label="Capacity" type="number" fullWidth value={formData.capacity || 1} onChange={(e) => setFormData({ ...formData, capacity: e.target.value })} />
                                <TextField select label="Type" fullWidth value={formData.type || 'Non-AC'} onChange={(e) => setFormData({ ...formData, type: e.target.value })}>
                                    <MenuItem value="Non-AC">Non-AC</MenuItem>
                                    <MenuItem value="AC">AC</MenuItem>
                                </TextField>
                            </>
                        )}

                        {dialogType === 'allocation' && (
                            <>
                                <TextField select label="Student" fullWidth value={formData.studentId || ''} onChange={(e) => setFormData({ ...formData, studentId: e.target.value })}>
                                    {students.map(s => <MenuItem key={s.id} value={s.id}>{s.firstName} {s.lastName} ({s.admissionNo})</MenuItem>)}
                                </TextField>
                                <TextField select label="Room" fullWidth value={formData.roomId || ''} onChange={(e) => setFormData({ ...formData, roomId: e.target.value })}>
                                    {rooms.filter(r => r.occupied < r.capacity).map(r => <MenuItem key={r.id} value={r.id}>{r.roomNumber} ({r.hostel?.name})</MenuItem>)}
                                </TextField>
                                <TextField label="Start Date" type="date" fullWidth InputLabelProps={{ shrink: true }} value={formData.startDate || ''} onChange={(e) => setFormData({ ...formData, startDate: e.target.value })} />
                            </>
                        )}

                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained">Save</Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default Hostel;
